import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { fetchResearchEntries, type ResearchEntry } from '../../services/researchApi';
import { ResearchInPracticeCard } from './ResearchInPracticeCard';
import { cn } from '../../utils/cn';

/*
 * ResearchInPracticeSection
 * -------------------------
 * "Research in practice" — expertise/R&D showcase strip that renders
 * near the bottom of the home page. Presents each entry from the
 * `in_parctice_research_and_documentsion` catalog as a card in a
 * horizontally-scrolling row.
 *
 * Framing intent
 * --------------
 * This section is designed to build client trust — "here's the
 * research and infrastructure we build behind the scenes so you're
 * getting a team that's already explored what's coming next" — NOT
 * to sound like a product catalog. The heading, eyebrow, and subline
 * all lean into the "proof of expertise" story, and the individual
 * cards use category chips (Research / Implementation) rather than
 * anything price-y or CTA-heavy.
 *
 * Behaviour
 * ---------
 * * Loads via GET /in_practice_research on mount. If the API returns
 *   nothing (or is unreachable) the whole section returns `null` so
 *   the home page has no empty gap.
 * * The card row is a native horizontally-scrollable container with
 *   `scroll-snap-type: x mandatory`, so touch / trackpad users get
 *   smooth swiping for free.
 * * Two round chevron buttons sit floating on the section's outer
 *   left / right edges. Clicking them programmatically scrolls the
 *   row one card at a time across repeated copies. An invisible reset
 *   between identical copies keeps motion continuous in either direction. Both disable only when every card already fits.
 * * The page height stays fixed — the row is `overflow-x-auto` with
 *   a hidden scrollbar (via utility classes), which means clicking
 *   arrows never pushes anything else on the page around.
 */

const HEADER_EYEBROW = 'Innovation & research';
const HEADER_TITLE = 'Research & Implementation in Practice';
const HEADER_SUBLINE =
  "A window into our research and implementation work — from the pipelines we prototype to the tools we refine through hands-on experience. Each project demonstrates our expertise, building trust and confidence in what we can deliver.";

export const ResearchInPracticeSection: React.FC = () => {
  const [entries, setEntries] = useState<ResearchEntry[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const cycleRef = useRef(0);
  const frameRef = useRef(0);
  const queueRef = useRef<number[]>([]);
  const movingRef = useRef(false);
  const advanceRef = useRef<(direction: number) => void>(() => {});

  // Both directions remain available whenever the row overflows.
  const [canScroll, setCanScroll] = useState(false);

  // Fetch on mount. Fail-open: on any error the local `entries` list
  // stays empty and this section returns `null` below.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await fetchResearchEntries();
      if (!cancelled) {
        setEntries(list);
        setIsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el || !entries.length) return;
    let settleTimer: ReturnType<typeof setTimeout>;
    const normalize = () => {
      const cycle = cycleRef.current;
      if (!cycle || movingRef.current) return;
      const left = el.scrollLeft - 4;
      const centered = cycle + ((left % cycle) + cycle) % cycle + 4;
      if (Math.abs(centered - el.scrollLeft) > 2) {
        el.scrollTo({ left: centered, behavior: 'instant' });
      }
    };
    const update = () => {
      cancelAnimationFrame(frameRef.current);
      movingRef.current = false;
      queueRef.current = [];
      const card = el.firstElementChild as HTMLElement;
      const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
      const cycle = (card.getBoundingClientRect().width + gap) * entries.length;
      const overflow = cycle - gap + 8 > el.clientWidth + 2;
      cycleRef.current = overflow ? cycle : 0;
      setCanScroll(overflow);
      el.style.scrollSnapType = '';
      el.scrollTo({ left: overflow ? cycle + 4 : 0, behavior: 'instant' });
    };
    advanceRef.current = (direction) => {
      if (!cycleRef.current) return;
      if (movingRef.current) {
        queueRef.current.push(direction);
        return;
      }
      normalize();
      const start = el.scrollLeft;
      const step = cycleRef.current / entries.length;
      const target = Math.round((start - 4) / step) * step + 4 + direction * step;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      movingRef.current = true;
      el.style.scrollSnapType = 'none';
      const started = performance.now();
      const tick = (now: number) => {
        const progress = reduced ? 1 : Math.min((now - started) / 420, 1);
        const eased = progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
        el.scrollTo({ left: start + (target - start) * eased, behavior: 'instant' });
        if (progress < 1) {
          frameRef.current = requestAnimationFrame(tick);
        } else {
          movingRef.current = false;
          normalize();
          el.style.scrollSnapType = '';
          const next = queueRef.current.shift();
          if (next !== undefined) advanceRef.current(next);
        }
      };
      frameRef.current = requestAnimationFrame(tick);
    };
    const onScroll = () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(normalize, 160);
    };
    const interrupt = () => {
      cancelAnimationFrame(frameRef.current);
      movingRef.current = false;
      queueRef.current = [];
      el.style.scrollSnapType = '';
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    observer.observe(el.firstElementChild!);
    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('pointerdown', interrupt);
    el.addEventListener('wheel', interrupt, { passive: true });
    return () => {
      observer.disconnect();
      clearTimeout(settleTimer);
      interrupt();
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('pointerdown', interrupt);
      el.removeEventListener('wheel', interrupt);
    };
  }, [entries, canScroll]);

  const scrollBy = useCallback((direction: 1 | -1) => advanceRef.current(direction), []);

  // Empty-state hide. Once the API resolves and returns zero rows
  // (either a real empty catalog or a Mongo-down fail-open), render
  // nothing at all so we don't leave a "coming soon" gap on the
  // homepage.
  if (isLoaded && entries.length === 0) return null;

  return (
    <section className="bg-white py-16 md:py-20 lg:py-24 overflow-hidden">
      <div className="site-wide-container">
        {/* ── Section header ─────────────────────────────────
            Left-anchored to match /services and /industries. */}
        <div className="max-w-3xl mb-8 md:mb-10">
          <motion.p
            className="text-sm md:text-base font-semibold uppercase tracking-[0.12em] text-accent-600 mb-4"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            {HEADER_EYEBROW}
          </motion.p>
          <motion.h2
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-navy-950 leading-[1.16] mb-4 md:mb-5"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.4, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
          >
            {HEADER_TITLE}
          </motion.h2>
          <motion.p
            className="text-base md:text-lg text-navy-700 leading-relaxed"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.4, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            {HEADER_SUBLINE}
          </motion.p>
        </div>

        {/* ── Slider ──────────────────────────────────────────
            Position: relative so the chevron buttons can absolutely
            sit on the row's own left/right edges without leaking
            into the section header above. */}
        <div className="relative">
          {/* Left chevron — pinned outside the scroll surface on
              desktop so the row itself stays visually flush. On
              small screens it overlaps the edge; that trade-off is
              fine because on mobile the primary interaction is
              swipe, not the arrow. */}
          <ArrowButton
            direction="left"
            onClick={() => scrollBy(-1)}
            disabled={!canScroll}
          />

          {/*
            The scroll surface itself. Arrow animation + snap points
            keep both programmatic scrolling (arrow clicks) and touch
            scrolling feeling deliberate.

            `.no-scrollbar` (styled via arbitrary Tailwind) hides the
            native scrollbar so the row reads as a slider — the
            arrows communicate scrollability, not a system scrollbar.
          */}
          <div
            ref={scrollerRef}
            className={cn(
              'flex flex-nowrap overflow-x-auto',
              // scroll-snap for a "click-to-panel" feel
              'snap-x snap-mandatory',
              // gap between cards; multiply into the arrow-scroll
              // delta calculation above
              'gap-5 md:gap-6',
              // hide scrollbars across engines
              '[scrollbar-width:none] [-ms-overflow-style:none] ' +
                '[&::-webkit-scrollbar]:hidden',
              // a little padding-inline so cards near the ends
              // don't hug the arrow buttons
              'px-1',
            )}
          >
            {(canScroll ? [0, 1, 2] : [1]).flatMap(copy => entries.map((entry, i) => (
              <ResearchInPracticeCard
                key={`${copy}-${entry.id}-${i}`}
                entry={entry}
                animateEntrance={false}
                duplicate={copy !== 1}
              />
            )))}
          </div>

          <ArrowButton
            direction="right"
            onClick={() => scrollBy(1)}
            disabled={!canScroll}
          />
        </div>
      </div>
    </section>
  );
};

/**
 * Round chevron button that sits on the outer edge of the slider.
 * Absolutely-positioned relative to the scroller's wrapper. When it
 * all cards fit in the row, both buttons disable and fade.
 */
const ArrowButton: React.FC<{
  direction: 'left' | 'right';
  onClick: () => void;
  disabled: boolean;
}> = ({ direction, onClick, disabled }) => {
  const isLeft = direction === 'left';
  return (
    <button
      type="button"
      aria-label={isLeft ? 'Scroll left' : 'Scroll right'}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'group absolute top-1/2 -translate-y-1/2 z-10',
        // Peel the button just off the row's edge on desktop so the
        // cards stay uninterrupted; on small screens it sits ~inside
        // the row so it's still tappable.
        isLeft ? 'left-0 md:-left-2 lg:-left-4' : 'right-0 md:-right-2 lg:-right-4',
        'flex items-center justify-center rounded-full',
        'w-10 h-10 md:w-11 md:h-11',
        'bg-navy-950 text-white',
        'shadow-[0_10px_25px_-8px_rgba(2,8,20,0.4)]',
        'hover:bg-accent-600 transition-colors',
        'disabled:opacity-30 disabled:pointer-events-none',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2',
      )}
    >
      {isLeft ? (
        <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
      ) : (
        <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
      )}
    </button>
  );
};
